import { DateTime } from "luxon";

import { MovementScreenConfig } from "../../ajax/ConfigAjax";
import { GeoRehandleDTO } from "../../DTOs/Geology/GeoRehandleDTO";
import { GeoRehandleTruckingTruckDTO } from "../../DTOs/Geology/GeoRehandleTruckingTruckDTO";
import { shiftDurationHours, shiftStart } from "../../offline/shiftBands";
import { TripleShift } from "../../types/Shift";

export interface ShiftWindow {
  start: DateTime;
  end: DateTime;
}

export interface MovementLayout {
  timeColumn: "Hour" | "Time" | null;
  carrierColumn: "Plant Model" | "Truck" | null;
  time: string;
  machine: string;
  carrier: string;
  movement: string;
}

export function movementShiftWindow(dateIso: string, shift: TripleShift, zone: string): ShiftWindow {
  const start = shiftStart(dateIso, shift).setZone(zone, { keepLocalTime: true });
  return { start, end: start.plus({ hours: shiftDurationHours(shift) }) };
}

// Note: a time picker yields a clock time whose date cannot be relied on, so the day comes from the shift. A
// clock time earlier than the shift's start is the morning after, which is how a night shift crosses midnight.
export function anchorTimeInShift(picked: DateTime, shiftWindow: ShiftWindow): DateTime {
  const sameDay = shiftWindow.start.set({ hour: picked.hour, minute: picked.minute, second: 0, millisecond: 0 });
  return sameDay < shiftWindow.start ? sameDay.plus({ days: 1 }) : sameDay;
}

// Note: an ISO string with no offset is UTC, which is how the API writes this column.
export function movementTimeInZone(movementTimeUtc: DateTime | string, zone: string): DateTime {
  return typeof movementTimeUtc === "string"
    ? DateTime.fromISO(movementTimeUtc, { zone: "utc" }).setZone(zone)
    : movementTimeUtc.setZone(zone);
}

// Note: a value the picker cannot show counts as missing, so a blank cell is never saved, unless the machine is a
// loader, which may carry its own load and so needs no truck. An empty list has not loaded yet and is left to
// the server, which checks the same list.
export function movementEntryErrors(
  row: GeoRehandleDTO,
  config: MovementScreenConfig,
  isNew: boolean,
  offered: { plantModels: GeoRehandleTruckingTruckDTO[]; trucks: GeoRehandleTruckingTruckDTO[]; carriesOwnLoad: boolean }
): string[] {
  const errors: string[] = [];
  const unlisted = (list: GeoRehandleTruckingTruckDTO[], matches: (option: GeoRehandleTruckingTruckDTO) => boolean) =>
    list.length > 0 && !list.some(matches);
  const hasModel = !!row.bucketTypeId && !unlisted(offered.plantModels, model => model.bucketTypeId === row.bucketTypeId);

  if (config.carriedBy === "PlantModel" && !hasModel && !offered.carriesOwnLoad)
    errors.push("Please select a Plant Model");
  if (config.carriedBy === "Plant"
    && (row.truckPlantId ? unlisted(offered.trucks, truck => truck.plantId === row.truckPlantId) : !offered.carriesOwnLoad))
    errors.push("Please select a Truck");
  if (isNew && config.timeEntry === "Hour" && !row.hour) errors.push("Please select an Hour");
  if (isNew && config.timeEntry === "Time" && !row.movementTimeUtc) errors.push("Please select a Time");

  return errors;
}

// Note: each layout adds up to the same 110% as the screens always have, and every class is written out whole
// so Tailwind can see it.
export function movementLayout(config: MovementScreenConfig): MovementLayout {
  const timeColumn = config.timeEntry === "Hour" ? "Hour" : config.timeEntry === "Time" ? "Time" : null;
  const carrierColumn = config.carriedBy === "PlantModel" ? "Plant Model" : config.carriedBy === "Plant" ? "Truck" : null;
  const columns: Pick<MovementLayout, "timeColumn" | "carrierColumn" | "time"> =
    { timeColumn, carrierColumn, time: "basis-[12%]" };

  if (carrierColumn && timeColumn)
    return { ...columns, machine: "basis-[14%]", carrier: "basis-[14%]", movement: "basis-[20%]" };
  if (carrierColumn)
    return { ...columns, machine: "basis-[20%]", carrier: "basis-[20%]", movement: "basis-[20%]" };
  if (timeColumn)
    return { ...columns, machine: "basis-[20%]", carrier: "basis-[20%]", movement: "basis-[24%]" };
  return { ...columns, machine: "basis-[20%]", carrier: "basis-[20%]", movement: "basis-[30%]" };
}

export function truckOptions(trucks: GeoRehandleTruckingTruckDTO[]): GeoRehandleTruckingTruckDTO[] {
  return [...trucks].sort((a, b) => a.plantRef.localeCompare(b.plantRef));
}

type PlantModelSource = { bucketTypeId: number, bucketType: string, outsideUsualJob?: boolean, modelInUse?: boolean };

export function plantModelOptions<T extends PlantModelSource>(trucks: T[]): T[] {
  const byModel = new Map<number, T>();
  for (const truck of trucks) {
    const kept = byModel.get(truck.bucketTypeId);
    if (!kept || (kept.outsideUsualJob && !truck.outsideUsualJob))
      byModel.set(truck.bucketTypeId, truck);
  }
  return [...byModel.values()]
    .map(model => model.outsideUsualJob && model.modelInUse ? { ...model, outsideUsualJob: false } : model)
    .sort((a, b) => a.bucketType.localeCompare(b.bucketType));
}
